import React, { Fragment } from 'react';
import { Col, Label } from 'reactstrap';
import Select from 'react-select';

/**
 * FlexInputBuilder — A standardized, state-controlled input renderer.
 *
 * Similar to RegisteredInputsBuilder but designed for:
 * - Controlled state (useState) instead of react-hook-form
 * - Dynamic/repeatable rows (orderBy, filters, etc.)
 * - Inline flex layouts alongside grid layouts
 *
 * Supports types: text, number, textarea, reactselect, multiselect, custom
 *
 * inputSize prop controls height of all inputs via CSS class:
 *   'sm' = 30px | 'md' = 34px | 'lg' = 38px (default)
 *
 * Usage (grid mode — like RegisteredInputsBuilder):
 *   <FlexInputBuilder inputSize='md' inputs={[
 *     { name: 'identifier', label: 'Identifier', type: 'text', value: identifier, onChange: setIdentifier, placeholder: '...' },
 *     { name: 'resource', label: 'Resource', type: 'reactselect', value: resourceId, onChange: setResourceId, options: [...] },
 *   ]} />
 *
 * Usage (inline mode — for dynamic rows):
 *   <FlexInputBuilder layout='inline' inputSize='md' inputs={[
 *     { name: 'field', type: 'text', value: row.field, onChange: (v) => update(idx, 'field', v), flex: '1 1 auto' },
 *     { name: 'direction', type: 'reactselect', value: row.direction, onChange: (v) => update(idx, 'direction', v), options: [...], width: 100 },
 *   ]} />
 */

// ─── Shared react-select JS styles (layout only — heights controlled by CSS class) ───
const baseSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: 'none' }),
    option: (base) => ({ ...base, padding: '6px 10px' }),
    menu: (base) => ({ ...base, zIndex: 10 }),
};

const multiSelectStyles = {
    ...baseSelectStyles,
};

// ─── Single Input Renderer ───
const FlexInput = ({ input, layout, form }) => {
    const {
        name, label, type = 'text', value, onChange, options = [],
        placeholder, required, readOnly, helpText,
        size = 12, labelSize = 4, inputSize = 8,
        flex, width, style = {}, isSearchable, closeMenuOnSelect,
        visible = true, render, inputMode,
        labelInline = false, labelWidth = 110,
    } = input;

    if (!visible) return null;

    const handleChange = (val) => {
        if (typeof onChange === 'function') onChange(val);
    };

    const renderInput = () => {
        switch (type) {
            case 'reactselect':
                return (
                    <div style={width ? { width, flexShrink: 0 } : { flex: '1 1 auto', minWidth: 0 }}>
                        <Select
                            className='react-select-container'
                            classNamePrefix='react-select'
                            options={options}
                            value={options.find(o => o.value === value) || null}
                            onChange={(opt) => handleChange(opt?.value ?? null)}
                            placeholder={placeholder}
                            styles={baseSelectStyles}
                            menuPortalTarget={document.body}
                            isSearchable={isSearchable !== undefined ? isSearchable : true}
                            isDisabled={readOnly}
                        />
                    </div>
                );

            case 'multiselect':
                return (
                    <Select
                        isMulti
                        closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : false}
                        className='react-select-container react-select-multi'
                        classNamePrefix='react-select'
                        options={options}
                        value={options.filter(o => (value || []).includes(o.value))}
                        onChange={(selected) => handleChange((selected || []).map(s => s.value))}
                        placeholder={placeholder}
                        styles={multiSelectStyles}
                        menuPortalTarget={document.body}
                        isDisabled={readOnly}
                    />
                );

            case 'textarea':
                return (
                    <textarea
                        className='form-control'
                        style={{ resize: 'vertical', minHeight: 60, ...style }}
                        value={value || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={placeholder}
                        readOnly={readOnly}
                    />
                );

            case 'checkbox':
                return (
                    <label className='d-flex align-items-center' htmlFor={`flex-cb-${name}`} style={{ gap: 8, fontSize: 13, fontWeight: 400, cursor: 'pointer', margin: 0, ...style }}>
                        {form ? (
                            <input
                                type='checkbox'
                                className='form-check-input'
                                id={`flex-cb-${name}`}
                                {...form.register(name)}
                                disabled={readOnly}
                                style={{ margin: 0 }}
                            />
                        ) : (
                            <input
                                type='checkbox'
                                className='form-check-input'
                                id={`flex-cb-${name}`}
                                checked={!!value}
                                onChange={(e) => handleChange(e.target.checked)}
                                disabled={readOnly}
                                style={{ margin: 0 }}
                            />
                        )}
                        {label}
                    </label>
                );

            case 'custom':
                return render || null;

            default: // text, number, email, etc.
                return (
                    <input
                        className='form-control'
                        type={type}
                        inputMode={inputMode}
                        style={{ ...(width ? { width } : {}), ...style }}
                        value={value ?? ''}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={placeholder}
                        readOnly={readOnly}
                    />
                );
        }
    };

    // ─── Inline Layout (flex row items) ───
    if (layout === 'inline') {
        return (
            <div style={{ flex: flex || undefined, ...(width && type !== 'reactselect' ? { width, flexShrink: 0 } : {}) }}>
                {label && <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2, display: 'block' }}>{label}{required && <span style={{ color: 'red' }}> *</span>}</label>}
                {renderInput()}
            </div>
        );
    }

    // ─── Grid Layout (column-based, like RegisteredInputsBuilder) ───
    if (input.topAligned) {
        return (
            <Col md={size} className='mb-2'>
                <Label style={{ fontSize: 12, fontWeight: 600, marginBottom: 2, paddingLeft: 3 }}>
                    {label}{required && <span style={{ color: 'red' }}> *</span>}
                </Label>
                {renderInput()}
                {helpText && <span style={{ color: 'gray', fontSize: 12, paddingLeft: 3, display: 'block', marginTop: 2 }}>{helpText}</span>}
            </Col>
        );
    }

    // labelInline → label sits beside the input (same small-tag styling).
    if (labelInline && label && type !== 'checkbox') {
        return (
            <Col md={size} className='mb-2'>
                <div className='d-flex align-items-center' style={{ gap: 10 }}>
                    <Label className='mb-0' style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', width: labelWidth }}>
                        {label}{required && <span style={{ color: 'red' }}> *</span>}
                    </Label>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>{renderInput()}</div>
                </div>
                {helpText && <span style={{ color: 'gray', fontSize: 12, display: 'block', marginTop: 2 }}>{helpText}</span>}
            </Col>
        );
    }

    return (
        <Col md={size} className='mb-2'>
            {label && type !== 'checkbox' && (
                <Label style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                    {label}{required && <span style={{ color: 'red' }}> *</span>}
                </Label>
            )}
            {renderInput()}
            {helpText && <span style={{ color: 'gray', fontSize: 12, display: 'block', marginTop: 2 }}>{helpText}</span>}
        </Col>
    );
};

// ─── Main Component ───
const FlexInputBuilder = ({ inputs, layout = 'grid', inputSize = 'lg', className = '', style = {}, gap = '0.25rem', form }) => {
    if (!inputs || inputs.length === 0) return null;

    const sizeClass = `input-${inputSize}`;

    if (layout === 'inline') {
        return (
            <div className={`d-flex gap-1 align-items-end ${sizeClass} ${className}`} style={{ gap, ...style }}>
                {inputs.map((input, idx) => (
                    <FlexInput key={input.name || idx} input={input} layout='inline' form={form} />
                ))}
            </div>
        );
    }

    // Grid layout — wraps in a row so inputs with size props flow side-by-side
    return (
        <div className={`row ${sizeClass} ${className}`} style={style}>
            {inputs.map((input, idx) => (
                <FlexInput key={input.name || idx} input={input} layout='grid' form={form} />
            ))}
        </div>
    );
};

// Export styles for cases where components need direct access
export { baseSelectStyles, multiSelectStyles };
export default FlexInputBuilder;
